/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useMemo, useCallback, useRef, useEffect} from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'

// Chakra Components
import {
    Box,
    Button,
    IconButton,
    Link,
    Flex,
    AspectRatio,
    Skeleton as ChakraSkeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import Badges from '../product-badges'
import {Mute, Pause, Play, Unmute} from '../icons'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

// Lazy load ProductZoomModal - only loaded when user clicks to zoom an image
// This defers Chakra Modal components until needed
const ProductZoomModal = loadable(() => import('../product-zoom-modal'), {ssr: false})

// Constants
const VIEW_TYPE_LARGE = 'large'
const VIEW_TYPE_GIFT_CARD = 'giftCard'
const VIEW_TYPE_ZOOM = 'zoom'
const VIEW_TYPE_VIDEO = 'video'
const SKELETON_ITEMS_COUNT = 7
const MOBILE_GAP = 4 // Gap between images in mobile carousel (px)
const DESKTOP_GAP = 8 // Gap between images in desktop grid (px)
export const DYNAMIC_IMAGE_MAX_WIDTH = '1280px'

// Image widths config for different grid positions
// The gallery takes ~50% of viewport, then images are 50% or 33% of that
// Desktop large: ~30vw, Desktop small: ~20vw
// Mobile: full width carousel
const IMAGE_WIDTHS = {
    // First 4 images (~30% of viewport on desktop)
    large: {base: '100vw', md: '50vw', lg: '30vw'},
    // Remaining images (~20% of viewport on desktop)
    small: {base: '100vw', md: '50vw', lg: '20vw'},
    // Mobile carousel (full width)
    mobile: {base: '100vw', md: '100vw', lg: '100vw'}
}

// Custom magnifying glass cursor using SVG data URL
const MAGNIFYING_GLASS_CURSOR = `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='rgba(255,255,255,0.85)' d='M0 0h28v28H0z'/%3E%3Cg clip-path='url(%23a)'%3E%3Cpath d='M12.33 9.833v5m-2.5-2.5h5m7.09 9.59-4.59-4.59m-5 1.667a6.667 6.667 0 1 0 0-13.334 6.667 6.667 0 0 0 0 13.334Z' stroke='%2325252B' stroke-width='1.1' stroke-linecap='square'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='a'%3E%3Cpath fill='%23fff' d='M4 4h20v20H4z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E") 12 12, zoom-in`

/**
 * Skeleton loading component for image gallery
 * Displays placeholder grid while images are loading
 */
export const Skeleton = () => {
    return (
        <Box data-testid="sf-image-gallery-skeleton">
            <Box
                display="grid"
                gridTemplateColumns="repeat(6, 1fr)"
                gap={`${DESKTOP_GAP}px`}
            >
                {Array.from({length: SKELETON_ITEMS_COUNT}, (_, index) => (
                    <Box key={index} gridColumn={index < 4 ? 'span 3' : 'span 2'}>
                        <AspectRatio ratio={2 / 3}>
                            <ChakraSkeleton />
                        </AspectRatio>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

Skeleton.propTypes = {
    size: PropTypes.string
}

/**
 * Individual image component with click handling and loading optimization
 * @param {boolean} isAboveFold - Whether image is above the fold (LCP candidate)
 * @param {Object} widths - Responsive widths config for DynamicImage
 */
const ImageItem = ({
    image,
    index,
    onImageClick,
    cursor,
    alt,
    loadingStrategy,
    getImageUrl,
    isAboveFold = false,
    widths = IMAGE_WIDTHS.large,
    imageProps = {},
    imageContainerProps = {}
}) => {
    return (
        <AspectRatio ratio={2 / 3} {...imageContainerProps}>
            <DynamicImage
                src={getImageUrl(image)}
                widths={widths}
                imageProps={{
                    alt: alt || image.alt,
                    loading: loadingStrategy,
                    decoding: isAboveFold ? 'sync' : 'async',
                    fetchpriority: isAboveFold ? 'high' : 'auto',
                    onClick: () => onImageClick?.(index),
                    style: {cursor, objectFit: 'cover'},
                    ...imageProps
                }}
            />
        </AspectRatio>
    )
}

ImageItem.propTypes = {
    image: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    onImageClick: PropTypes.func,
    cursor: PropTypes.string,
    alt: PropTypes.string,
    loadingStrategy: PropTypes.oneOf(['lazy', 'eager']).isRequired,
    getImageUrl: PropTypes.func.isRequired,
    isAboveFold: PropTypes.bool,
    widths: PropTypes.object,
    imageProps: PropTypes.object,
    imageContainerProps: PropTypes.object
}

/**
 * Video item that lazily loads and plays only when visible in viewport (desktop)
 * or when it is the active carousel slide (mobile).
 */
const VideoGalleryItem = ({image, isActiveSlide, wrapperStyle, elementStyle}) => {
    const videoRef = useRef(null)
    const [isInViewport, setIsInViewport] = useState(false)

    // Intersection observer drives playback on desktop grid
    useEffect(() => {
        const video = videoRef.current
        if (!video) return
        const observer = new IntersectionObserver(
            ([entry]) => setIsInViewport(entry.isIntersecting),
            {threshold: 0.5}
        )
        observer.observe(video)
        return () => observer.disconnect()
    }, [])

    // Play when active slide (mobile) OR in viewport (desktop)
    const shouldPlay = isActiveSlide || isInViewport
    // Ref mirrors current shouldPlay so the onCanPlay handler avoids stale closures
    const shouldPlayRef = useRef(shouldPlay)
    useEffect(() => {
        shouldPlayRef.current = shouldPlay
    }, [shouldPlay])

    const [isMuted, setIsMuted] = useState(true)
    const [isPlaying, setIsPlaying] = useState(true)

    useEffect(() => {
        const video = videoRef.current
        if (!video) return
        // Set both the DOM property AND the HTML attribute:
        // - DOM property (video.muted) is checked by Chrome/Firefox at play() time
        // - HTML attribute (setAttribute) is checked by Safari for autoplay policy
        // Both must be set because React's reconciliation may reset the DOM property
        // between our imperative set and when the browser evaluates the async play() call.
        video.muted = isMuted
        if (isMuted) {
            video.setAttribute('muted', '')
        } else {
            video.removeAttribute('muted')
        }
        if (shouldPlay) {
            video.play().catch(() => {})
        } else {
            video.pause()
        }
    }, [shouldPlay, isMuted])

    // Retry play() once the browser signals it has enough data to start.
    // Guards against the race condition where play() is called before buffering begins.
    const handleCanPlay = useCallback(() => {
        const video = videoRef.current
        if (!video || !shouldPlayRef.current || !video.paused) return
        video.play().catch(() => {})
    }, [])

    const togglePlayPause = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()
        const video = videoRef.current
        if (!video) return
        if (video.paused) {
            video.play().catch(() => {})
        } else {
            video.pause()
        }
    }, [])

    const toggleMute = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            const nextMuted = !isMuted
            const video = videoRef.current
            if (video) {
                video.muted = nextMuted
                if (nextMuted) video.setAttribute('muted', '')
                else video.removeAttribute('muted')
            }
            setIsMuted(nextMuted)
        },
        [isMuted]
    )

    const [showControls, setShowControls] = useState(false)

    return (
        <Box
            {...wrapperStyle}
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
            onClick={() => setShowControls((prev) => !prev)}
        >
            {/* Use a native <video> element — Chakra's <Box as="video"> can silently drop
                src/muted/loop/playsInline when those props conflict with its style system.
                autoPlay + muted is the browser-native muted-autoplay path (always allowed
                by browser autoplay policy) and matches the existing working Video component. */}
            <video
                ref={videoRef}
                autoPlay
                muted={isMuted}
                loop
                playsInline
                preload="none"
                aria-label={image.alt || 'Product video'}
                onCanPlay={handleCanPlay}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                style={elementStyle}
            >
                <source src={image.src} type="video/mp4" />
            </video>
            {showControls && (
                <Box
                    position="absolute"
                    top="8px"
                    right="8px"
                    display="flex"
                    flexDirection="column"
                    gap="12px"
                    zIndex={2}
                >
                    <IconButton
                        aria-label={isPlaying ? 'Pause' : 'Play'}
                        onClick={togglePlayPause}
                        variant="unstyled"
                        boxSize="24px"
                        minW="24px"
                        h="24px"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        color="white"
                        _hover={{bg: 'transparent'}}
                        _active={{bg: 'transparent'}}
                        _focus={{boxShadow: 'none'}}
                        icon={isPlaying ? <Pause /> : <Play />}
                    />
                    <IconButton
                        aria-label={isMuted ? 'Unmute' : 'Mute'}
                        onClick={toggleMute}
                        variant="unstyled"
                        boxSize="24px"
                        minW="24px"
                        h="24px"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        color="white"
                        _hover={{bg: 'transparent'}}
                        _active={{bg: 'transparent'}}
                        _focus={{boxShadow: 'none'}}
                        icon={isMuted ? <Unmute /> : <Mute />}
                    />
                </Box>
            )}
        </Box>
    )
}

VideoGalleryItem.propTypes = {
    image: PropTypes.shape({
        src: PropTypes.string.isRequired,
        alt: PropTypes.string
    }).isRequired,
    isActiveSlide: PropTypes.bool
}

/**
 * Responsive gallery component - single DOM structure
 * Mobile (base to lg): horizontal scroll carousel with custom scrollbar
 * Desktop (lg+): CSS grid with 6 columns (first 4 images span 3, rest span 2)
 */
const ResponsiveGallery = ({
    images,
    badges,
    onImageClick,
    onSlideChange,
    getImageUrl,
    loadingStrategy,
    imageContainerProps,
    imageProps,
    videoItemStyle,
    videoElementStyle
}) => {
    const scrollContainerRef = useRef(null)
    const [scrollProgress, setScrollProgress] = useState(0)
    const [activeIndex, setActiveIndex] = useState(0)

    // Update scroll progress and active index (mobile only)
    const updateScrollState = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const {scrollLeft, scrollWidth, clientWidth} = container
        const maxScroll = scrollWidth - clientWidth

        // Calculate scroll progress (0 to 1)
        const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0
        setScrollProgress(progress)

        // Calculate active slide index
        const slideWidth = clientWidth + MOBILE_GAP
        const newActiveIndex = Math.round(scrollLeft / slideWidth)

        if (newActiveIndex !== activeIndex && newActiveIndex >= 0 && newActiveIndex < images.length) {
            setActiveIndex(newActiveIndex)
            onSlideChange?.({activeIndex: newActiveIndex})
        }
    }, [activeIndex, images.length, onSlideChange])

    // Set up scroll listener for mobile carousel
    useEffect(() => {
        const container = scrollContainerRef.current
        if (!container) return

        updateScrollState()
        container.addEventListener('scroll', updateScrollState, {passive: true})
        return () => container.removeEventListener('scroll', updateScrollState)
    }, [updateScrollState])

    // Calculate scrollbar thumb width for mobile
    const scrollbarThumbWidth = images.length > 0 ? 100 / images.length : 100

    return (
        <Box position="relative">
            {/* Badges - positioned outside grid/carousel to not affect nth-of-type counting */}
            {/* Mobile: offset by -16px margin + 8px padding = -8px from edge */}
            <Box
                position="absolute"
                top={1}
                left={{base: '-12px', lg: 1}}
                zIndex={1}
            >
                <Badges badges={badges} />
            </Box>

            {/* Single container that switches between carousel (mobile) and grid (desktop) */}
            <Box
                ref={scrollContainerRef}
                sx={{
                    // Mobile: horizontal scroll carousel
                    display: 'flex',
                    gap: `${MOBILE_GAP}px`,
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    scrollBehavior: 'smooth',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': {display: 'none'},
                    // Mobile: each item is full width
                    '& > *': {
                        flexShrink: 0,
                        width: '100vw'
                    },
                    // Desktop (lg+): switch to CSS grid
                    '@media (min-width: 62em)': {
                        display: 'grid',
                        gridTemplateColumns: 'repeat(6, 1fr)',
                        gap: `${DESKTOP_GAP}px`,
                        overflowX: 'visible',
                        // Desktop: items use grid column spans
                        '& > *': {
                            width: 'auto'
                        },
                        // First 4 images span 3 columns each
                        '& > *:nth-of-type(-n+4)': {
                            gridColumn: 'span 3'
                        },
                        // Remaining images span 2 columns each
                        '& > *:nth-of-type(n+5)': {
                            gridColumn: 'span 2'
                        }
                    }
                }}
                mx={{base: '-16px', lg: 0}}
            >
                {images.map((image, index) => (
                    <Box
                        key={`gallery-${index}`}
                        position="relative"
                    >
                        {image.isVideo ? (
                            <VideoGalleryItem
                                image={image}
                                isActiveSlide={activeIndex === index}
                                wrapperStyle={videoItemStyle}
                                elementStyle={videoElementStyle}
                            />
                        ) : (
                            <Button
                                as={Link}
                                variant="unstyled"
                                href="#"
                                cursor={MAGNIFYING_GLASS_CURSOR}
                                onClick={(e) => {
                                    e.preventDefault()
                                    // Map back to zoom-image index (skip video items)
                                    const imageOnlyIndex = images
                                        .slice(0, index)
                                        .filter(img => !img.isVideo).length
                                    onImageClick(imageOnlyIndex)
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        const imageOnlyIndex = images
                                            .slice(0, index)
                                            .filter(img => !img.isVideo).length
                                        onImageClick(imageOnlyIndex)
                                    }
                                }}
                                aria-label={`View larger image ${index + 1} of ${images.length}`}
                                display="block"
                                width="100%"
                                height="unset"
                            >
                                <ImageItem
                                    image={image}
                                    index={index}
                                    loadingStrategy={index < 4 ? 'eager' : loadingStrategy}
                                    getImageUrl={getImageUrl}
                                    isAboveFold={index < 4}
                                    widths={index < 4 ? IMAGE_WIDTHS.large : IMAGE_WIDTHS.small}
                                    imageProps={{
                                        width: '100%',
                                        ...imageProps
                                    }}
                                    imageContainerProps={{...imageContainerProps}}
                                />
                            </Button>
                        )}
                    </Box>
                ))}
            </Box>

            {/* Custom scrollbar for mobile - hidden on desktop */}
            {images.length > 1 && (
                <Box
                    display={{base: 'block', lg: 'none'}}
                    position="relative"
                    height="3px"
                    backgroundColor="transparent"
                    mx="-16px"
                >
                    <Box
                        position="absolute"
                        top={0}
                        left={`calc(${scrollProgress} * (100% - min(${scrollbarThumbWidth}%, 40px)))`}
                        width={`${scrollbarThumbWidth}%`}
                        maxWidth="40px"
                        height="3px"
                        backgroundColor="webBasic.600"
                        borderRadius="9999px"
                    />
                </Box>
            )}
        </Box>
    )
}

ResponsiveGallery.propTypes = {
    images: PropTypes.array.isRequired,
    badges: PropTypes.array,
    onImageClick: PropTypes.func.isRequired,
    onSlideChange: PropTypes.func.isRequired,
    getImageUrl: PropTypes.func.isRequired,
    loadingStrategy: PropTypes.oneOf(['lazy', 'eager']).isRequired,
    imageContainerProps: PropTypes.object,
    imageProps: PropTypes.object,
    videoItemStyle: PropTypes.object,
    videoElementStyle: PropTypes.object
}

/**
 * Gift gallery component - desktop shows single image, mobile shows carousel
 * Uses CSS-based responsive switching for zero CLS
 */
const GiftGallery = ({images, badges, getImageUrl, loadingStrategy}) => {
    if (!images.length) return null

    return (
        <>
            {/* Desktop: single centered image */}
            <Flex
                display={{base: 'none', lg: 'flex'}}
                justifyContent="center"
                position="relative"
                width="100%"
            >
                <Badges badges={badges} />
                <Box width="100%" maxWidth="600px">
                    <ImageItem
                        image={images[0]}
                        index={0}
                        loadingStrategy={loadingStrategy}
                        getImageUrl={getImageUrl}
                        imageContainerProps={{
                            ratio: { lg: 1 }
                        }}
                        imageProps={{
                            borderRadius: {lg: '16px'},
                            width: { lg: '100%' }
                        }}
                    />
                </Box>
            </Flex>
            {/* Mobile: carousel */}
            <Box display={{base: 'block', lg: 'none'}}>
                <ResponsiveGallery
                    images={images}
                    badges={badges}
                    onImageClick={() => {}}
                    onSlideChange={() => {}}
                    getImageUrl={getImageUrl}
                    loadingStrategy={loadingStrategy}
                    imageProps={{
                        borderRadius: {base: '10px'}
                    }}
                    imageContainerProps={{
                        mx: {base: '64px'}
                    }}
                />
            </Box>
        </>
    )
}

GiftGallery.propTypes = {
    images: PropTypes.array.isRequired,
    badges: PropTypes.array,
    getImageUrl: PropTypes.func.isRequired,
    loadingStrategy: PropTypes.oneOf(['lazy', 'eager']).isRequired
}

/**
 * Main ImageGallery component
 * Renders responsive image gallery with zoom modal functionality
 * Single DOM structure that uses CSS to switch between:
 * - Mobile: horizontal scroll carousel with custom scrollbar
 * - Desktop: CSS grid layout
 */
const ImageGallery = ({
    imageGroups = [],
    selectedVariationAttributes = {},
    size,
    lazy = false,
    badges = [],
    isGiftPage = false,
    videoSequencePositions = null
}) => {
    // Hooks
    const styles = useMultiStyleConfig('ImageGallery', {size})

    // State
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)

    // Memoized values
    const heroImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: isGiftPage ? VIEW_TYPE_GIFT_CARD : VIEW_TYPE_LARGE,
                selectedVariationAttributes
            }),
        [imageGroups, selectedVariationAttributes]
    )

    const zoomImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: VIEW_TYPE_ZOOM,
                selectedVariationAttributes
            }),
        [imageGroups, selectedVariationAttributes]
    )

    const heroImages = useMemo(() => heroImageGroup?.images || [], [heroImageGroup])
    const zoomImages = useMemo(
        () => zoomImageGroup?.images || heroImages,
        [zoomImageGroup, heroImages]
    )

    // Build merged images list with videos inserted at specified positions
    const mergedImages = useMemo(() => {
        if (!videoSequencePositions || !heroImages.length) return heroImages

        // Parse positions (1-indexed) from string like "1,4"
        const positions = String(videoSequencePositions)
            .split(',')
            .map((p) => parseInt(p.trim(), 10))
            .filter((p) => !isNaN(p) && p >= 1)

        if (!positions.length) return heroImages

        // Find video groups for the selected color
        const selectedColor = selectedVariationAttributes?.color
        const videoGroup = imageGroups.find((group) => {
            if (group.viewType !== VIEW_TYPE_VIDEO) return false
            if (!selectedColor) return true
            const colorAttr = group.variationAttributes?.find((a) => a.id === 'color')
            return colorAttr?.values?.some((v) => v.value === selectedColor)
        })

        if (!videoGroup?.images?.length) return heroImages

        // Build video items from the group
        const videoItems = videoGroup.images.map((img) => ({
            isVideo: true,
            src: img.link,
            alt: img.alt,
            title: img.title
        }))

        // Each position[i] is the 1-indexed slot in the FINAL merged sequence for videoItems[i].
        // Build the result by placing videos at their absolute positions, then
        // filling remaining slots with images in their original order.
        const totalLength = heroImages.length + videoItems.length
        const result = new Array(totalLength)
        positions.forEach((pos, i) => {
            if (videoItems[i] && pos >= 1 && pos <= totalLength) {
                result[pos - 1] = videoItems[i]
            }
        })
        let imgIdx = 0
        for (let i = 0; i < totalLength; i++) {
            if (!result[i]) result[i] = heroImages[imgIdx++]
        }
        return result.filter(Boolean)
    }, [heroImages, videoSequencePositions, selectedVariationAttributes, imageGroups])

    const loadingStrategy = useMemo(() => (lazy ? 'lazy' : 'eager'), [lazy])
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const PRODUCT_IMGS_RESIZE_PARAMS_ENABLED = getSiteEnvVarValue(appConfig, 'PRODUCT_IMGS_RESIZE_PARAMS_ENABLED', site?.id) == 'true'

    // Callback functions
    const getImageUrl = useCallback((image) => {
        const baseUrl = image.disBaseLink || image.link
        return PRODUCT_IMGS_RESIZE_PARAMS_ENABLED
        ? `${baseUrl}[?sw={width}&q=75&fmt=webp]`
        : baseUrl
    }, [])

    const handleImageClick = useCallback((imageIndex) => {
        setSelectedImageIndex(imageIndex)
        setIsModalOpen(true)
    }, [])

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false)
    }, [])

    const handleSlideChange = useCallback((data) => {
        setSelectedImageIndex(data.activeIndex)
    }, [])

    if (isGiftPage) {
        return (
            <Flex
                direction="column"
                alignItems="center"
                justifyContent="center"
                h={{base: 'auto', lg: '100%'}}
                minH={{base: 'auto', lg: 'calc(100vh - 80px)'}}
                bg="gray.200"
            >
                <Box {...styles.heroImageGroup} alignContent="center" w="100%">
                    <GiftGallery
                        images={heroImages}
                        badges={badges}
                        getImageUrl={getImageUrl}
                        loadingStrategy={loadingStrategy}
                    />
                </Box>
            </Flex>
        )
    }

    return (
        <Flex direction="column">
            <Box {...styles.heroImageGroup}>
                <ResponsiveGallery
                    images={mergedImages}
                    badges={badges}
                    onImageClick={handleImageClick}
                    onSlideChange={handleSlideChange}
                    getImageUrl={getImageUrl}
                    loadingStrategy={loadingStrategy}
                    videoItemStyle={styles.videoGalleryWrapper}
                    videoElementStyle={styles.videoGalleryElement}
                />
            </Box>

            {/* Zoom modal for enlarged image viewing */}
            <ProductZoomModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                images={zoomImages}
                thumbImages={heroImages}
                initialImageIndex={selectedImageIndex}
                getImageUrl={getImageUrl}
            />
        </Flex>
    )
}

ImageGallery.propTypes = {
    imageGroups: PropTypes.array,
    selectedVariationAttributes: PropTypes.object,
    size: PropTypes.string,
    lazy: PropTypes.bool,
    badges: PropTypes.array,
    isGiftPage: PropTypes.bool,
    videoSequencePositions: PropTypes.string
}

export default ImageGallery
